import React from 'react';
import { BaseModal } from './BaseModal';
import type { BaseModalProps } from './BaseModal';
import { Button } from '../Button';
import { 
  HiExclamationTriangle, 
  HiInformationCircle, 
  HiCheckCircle,
  HiXCircle
} from 'react-icons/hi2';

export interface AlertModalProps extends Omit<BaseModalProps, 'children'> {
  type?: 'info' | 'warning' | 'error' | 'success';
  message: string;
  details?: string;
  buttonText?: string;
  onClose: () => void;
}

export const AlertModal: React.FC<AlertModalProps> = ({
  type = 'info',
  title,
  message,
  details,
  buttonText = 'OK',
  onClose,
  isOpen,
  ...props
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <HiExclamationTriangle className="w-6 h-6 text-yellow-500" />;
      case 'error':
        return <HiXCircle className="w-6 h-6 text-red-500" />;
      case 'success':
        return <HiCheckCircle className="w-6 h-6 text-green-500" />;
      default:
        return <HiInformationCircle className="w-6 h-6 text-blue-500" />;
    }
  };

  const getButtonVariant = () => {
    switch (type) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'secondary';
      case 'success':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'warning':
        return 'Atenção';
      case 'error':
        return 'Erro';
      case 'success':
        return 'Sucesso';
      default:
        return 'Informação';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'warning':
        return 'Verifique as informações antes de prosseguir.';
      case 'error':
        return 'Ocorreu um problema que precisa de atenção.';
      case 'success':
        return 'A operação foi concluída com sucesso.';
      default:
        return 'Informação importante para sua atenção.';
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title || getDefaultTitle()}
      description={details || getDescription()}
      icon={getIcon()}
      useStandardLayout={true}
      size="sm"
      closeOnOverlayClick={true}
      closeOnEscape={true}
      {...props}
    >
      <div className="p-4">
        {/* Message */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
            {message}
          </p>
        </div>

        {/* Action Button */}
        <div className="flex items-center justify-end">
          <Button
            variant={getButtonVariant()}
            size="sm"
            onClick={onClose}
            autoFocus
            className="min-w-24"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};
